import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { ArrowLeft, FileText, Download, Loader2, Calendar, ExternalLink } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface Attestation {
  id: string;
  year: number;
  "Numéro de l'attestation": string;
  "Nom enfant": string;
  "Prénom enfant": string;
  "Id PDF": string;
  "Montant": string;
  "Nb jour(s)": string;
  "Date début": string;
  "Date fin": string;
}

const AttestationsPage = () => {
  const { user } = useAuthStore();
  const [attestations, setAttestations] = useState<Attestation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      fetchAttestations();
    }
  }, [user]);

  const fetchAttestations = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('Attestations fiscales')
        .select('id, year, "Numéro de l\'attestation", "Nom enfant", "Prénom enfant", "Id PDF", "Montant", "Nb jour(s)", "Date début", "Date fin"')
        .order('year', { ascending: false });

      if (fetchError) throw fetchError;
      setAttestations(data || []);
    } catch (err: any) {
      console.error('Error fetching attestations:', err);
      setError('Impossible de charger vos attestations fiscales.');
    } finally {
      setIsLoading(false);
    }
  };

  const groupedByYear = attestations.reduce<Record<number, Attestation[]>>((acc, att) => {
    const y = att.year;
    if (!acc[y]) acc[y] = [];
    acc[y].push(att);
    return acc;
  }, {});

  const sortedYears = Object.keys(groupedByYear)
    .map(Number)
    .sort((a, b) => b - a);

  const getDownloadUrl = (fileId: string) =>
    `https://drive.google.com/uc?export=download&id=${fileId}`;

  const getViewUrl = (fileId: string) =>
    `https://drive.google.com/file/d/${fileId}/view`;

  return (
    <div className="container max-w-6xl mx-auto py-12 px-4">
      <Link to="/dashboard" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-6">
        <ArrowLeft className="h-5 w-5 mr-2" />
        Retour au tableau de bord
      </Link>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <h1 className="text-3xl font-bold">Mes attestations fiscales</h1>
        <div className="flex items-center text-sm text-gray-500">
          <FileText className="h-4 w-4 mr-1" />
          {attestations.length} attestation{attestations.length > 1 ? 's' : ''} disponible{attestations.length > 1 ? 's' : ''}
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
        </div>
      ) : error ? (
        <div className="bg-red-50 p-4 rounded-lg">
          <p className="text-red-700">{error}</p>
        </div>
      ) : attestations.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-8 text-center">
          <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600 mb-2">Aucune attestation fiscale disponible.</p>
          <p className="text-gray-500 text-sm">
            Les attestations seront mises en ligne lorsqu'elles seront disponibles.
          </p>
        </div>
      ) : (
        <div className="space-y-8">
          {sortedYears.map((year) => (
            <div key={year}>
              <div className="flex items-center mb-4">
                <Calendar className="h-5 w-5 text-primary-600 mr-2" />
                <h2 className="text-xl font-semibold">Exercice {year}</h2>
                <span className="ml-3 text-sm text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                  {groupedByYear[year].length} document{groupedByYear[year].length > 1 ? 's' : ''}
                </span>
              </div>

              <div className="space-y-3">
                {groupedByYear[year].map((att) => (
                  <div
                    key={att.id}
                    className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 hover:shadow-md transition-shadow"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center mb-1">
                          <FileText className="h-4 w-4 text-primary-500 mr-2 flex-shrink-0" />
                          <p className="font-semibold text-gray-900 truncate">
                            {att["Prénom enfant"]} {att["Nom enfant"]}
                          </p>
                        </div>
                        <div className="ml-6 space-y-0.5">
                          <p className="text-sm text-gray-600">
                            Attestation n{"\u00B0"}{att["Numéro de l'attestation"]}
                          </p>
                          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500">
                            {att["Nb jour(s)"] && (
                              <span>{att["Nb jour(s)"]} jour{Number(att["Nb jour(s)"]) > 1 ? 's' : ''} de garde</span>
                            )}
                            {att["Montant"] && (
                              <span className="font-medium text-gray-700">{att["Montant"]} EUR</span>
                            )}
                          </div>
                          {att["Date début"] && att["Date fin"] && (
                            <p className="text-xs text-gray-400">
                              Periode: {att["Date début"]} - {att["Date fin"]}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2 sm:flex-shrink-0">
                        <a
                          href={getViewUrl(att["Id PDF"])}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-outline py-2 px-3 flex items-center text-sm"
                        >
                          <ExternalLink className="h-4 w-4 mr-1" />
                          Voir
                        </a>
                        <a
                          href={getDownloadUrl(att["Id PDF"])}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-primary py-2 px-3 flex items-center text-sm"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Telecharger
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-8 bg-blue-50 p-4 rounded-lg border border-blue-200">
        <div className="flex items-start">
          <FileText className="h-5 w-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-blue-800 font-medium">Information</p>
            <p className="text-blue-700 text-sm mt-1">
              Ces attestations fiscales sont des documents officiels a conserver pour votre declaration d'impots.
              Elles attestent des frais de garde engages pour vos enfants.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttestationsPage;
